/**
 * Démo guidée — visite automatisée de bout en bout pour présentation / formation.
 * Navigation assistée : la visite déplace l'utilisateur à travers les modules clés
 * avec une carte explicative, en avançant manuellement ou automatiquement.
 * Self-contained (aucune lib externe) ; monté globalement dans DashboardLayout.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { matchRoutes, useNavigate } from "react-router";
import { router } from "@/app/router";
import {
  RECORDER_UNSUPPORTED_MSG,
  formatElapsed,
  routeSlug,
  todayStamp,
  useScreenRecorder,
} from "./DemoRecorder";
import { useT, tr } from '@/i18n';
import { useUIStore } from '@/stores/ui-store';

interface Step {
  route: string;
  title: string;
  body: string;
  /**
   * Étape pointant une route livrée dans une branche parallèle : elle peut ne
   * PAS exister dans ce build. Dans ce cas la démo bascule sur `fallback` (ou
   * reste sur place) et affiche quand même la carte explicative — jamais
   * d'erreur, jamais de page « introuvable » au milieu de la visite.
   */
  optional?: boolean;
  /** Route de repli visitée si `route` n'est pas enregistrée dans ce build. */
  fallback?: string;
}

/**
 * Vrai si `path` correspond à une route réellement enregistrée dans ce build.
 * Tolérant : toute erreur d'introspection renvoie `false` (on bascule alors sur
 * le repli), de sorte que la démo ne lève jamais d'exception.
 */
function routeIsRegistered(path: string): boolean {
  try {
    return Boolean(matchRoutes(router.routes, path));
  } catch {
    return false;
  }
}

/**
 * Route à visiter pour une étape : la cible si elle existe, sinon son repli
 * (quand il existe), sinon la cible telle quelle (React Router rendra la page
 * « introuvable » sans planter). Les étapes non optionnelles sont toujours
 * visitées telles quelles.
 */
function resolveStepRoute(step: Step): string {
  if (!step.optional || routeIsRegistered(step.route)) return step.route;
  if (step.fallback && routeIsRegistered(step.fallback)) return step.fallback;
  return step.fallback ?? step.route;
}

/**
 * Stacking of the active tour (its panel, REC bar and error banner): above any
 * Modal. Some steps land on routes rendered AS a modal (e.g. /settings/notifications
 * → the notification-preferences Modal at z 10000); with the old z-50 the modal
 * covered « Suivant / Précédent / Terminer » and the tour could not continue.
 */
export const TOUR_Z = 10_050;

export const STEPS: Step[] = [
  // --- Accueil ---
  {
    route: "/",
    title: tr('shell.features.demoDemoTour.dashboard'),
    body: tr('shell.features.demoDemoTour.overviewOfYourActivityContentEngagement'),
  },
  {
    route: "/recommandations",
    title: tr('shell.features.demoDemoTour.aiRecommendations'),
    body: tr('shell.features.demoDemoTour.yourNextBestActionsPrioritizedBy'),
  },
  {
    route: "/strategy-mktg",
    title: tr('shell.features.demoDemoTour.marketingStrategy'),
    body: tr('shell.features.demoDemoTour.fromASimpleBriefTheAi'),
  },
  {
    route: "/projects",
    title: tr('shell.features.demoDemoTour.projectsCollaboration'),
    body: tr('shell.features.demoDemoTour.aiLaunchPlanChecklistTeamAnd'),
  },

  // --- Contenu ---
  {
    route: "/content",
    title: tr('shell.features.demoDemoTour.content'),
    body: tr('shell.features.demoDemoTour.createEditAndGetYourPosts'),
  },
  {
    route: "/content/new",
    title: tr('shell.features.demoDemoTour.newContent'),
    body: tr('shell.features.demoDemoTour.composeAMultiNetworkPostThe'),
  },
  {
    route: "/content/ideas",
    title: tr('shell.features.demoDemoTour.ideaFeed'),
    body: tr('shell.features.demoDemoTour.captureAnIdeaOrPasteA'),
  },
  {
    route: "/content/approvals",
    title: tr('shell.features.demoDemoTour.approvalsWorkflow'),
    body: tr('shell.features.demoDemoTour.configureTheMultiStepApprovalChain'),
  },
  {
    route: "/media-library",
    title: tr('shell.features.demoDemoTour.mediaLibrary'),
    body: tr('shell.features.demoDemoTour.yourCentralDamEditableImagesVideos'),
  },

  // --- Création / IA studios ---
  {
    route: "/content/studio",
    title: tr('shell.features.demoDemoTour.richContentStudio'),
    body: tr('shell.features.demoDemoTour.aiWritingHooksHashtagsViralityScore'),
  },
  {
    route: "/calendar",
    title: tr('shell.features.demoDemoTour.contentLevelRegeneration'),
    body: tr('shell.features.demoDemoTour.openAPostFromTheCalendar'),
  },
  {
    route: "/calendar",
    title: tr('shell.features.demoDemoTour.visualStyleGallery'),
    body: tr('shell.features.demoDemoTour.inThePostChooseAVisual'),
  },
  {
    route: "/content/diagram-studio",
    title: tr('shell.features.demoDemoTour.diagramEditorDrawIo'),
    body: tr('shell.features.demoDemoTour.createEditADiagramOpensThe'),
  },
  {
    route: "/content/diagram-studio",
    title: tr('shell.features.demoDemoTour.exactDiagramRenderedTemplate'),
    body: tr('shell.features.demoDemoTour.forAPreciseReproducibleChartDeterministi'),
  },
  {
    route: "/content/source-studio",
    optional: true,
    fallback: "/content",
    title: tr('shell.features.demoDemoTour.sourceStudioNotebooklmStyle'),
    body: tr('shell.features.demoDemoTour.ingestYourSourcesDocumentsLinksNotes'),
  },
  {
    route: "/content/unified",
    optional: true,
    fallback: "/content",
    title: tr('shell.features.demoDemoTour.unifiedStudioAutoRepurpose'),
    body: tr('shell.features.demoDemoTour.oneIdeaTheWholePackPost'),
  },
  {
    route: "/content/ai-studio",
    title: tr('shell.features.demoDemoTour.aiMediaStudio'),
    body: tr('shell.features.demoDemoTour.generativeEditingEraseOrReplaceAn'),
  },
  {
    route: "/content/video-studio",
    title: tr('shell.features.demoDemoTour.videoAudioStudio'),
    body: tr('shell.features.demoDemoTour.transcriptionReShareableSubtitledClipsTt'),
  },
  {
    route: "/content/carousel",
    title: tr('shell.features.demoDemoTour.carouselEditor'),
    body: tr('shell.features.demoDemoTour.buildACarouselSlideBySlide'),
  },
  {
    route: "/design",
    title: tr('shell.features.demoDemoTour.designCarousels'),
    body: tr('shell.features.demoDemoTour.designAndCarouselEditorForReady'),
  },
  {
    route: "/design/brand-kits",
    title: tr('shell.features.demoDemoTour.brandKits'),
    body: tr('shell.features.demoDemoTour.centralizeLogosColorsAndFontsEvery'),
  },
  {
    route: "/bio",
    title: tr('shell.features.demoDemoTour.linkInBioFunnels'),
    body: tr('shell.features.demoDemoTour.blockBasedPublicPagesHeroForm'),
  },

  // --- Planifier & Publier ---
  {
    route: "/calendar",
    title: tr('shell.features.demoDemoTour.calendar'),
    body: tr('shell.features.demoDemoTour.scheduleYourMultiNetworkPostsAnd'),
  },
  {
    route: "/planning/generate",
    title: tr('shell.features.demoDemoTour.generateACalendar'),
    body: tr('shell.features.demoDemoTour.n4StepAssistantDescribeTheMode'),
  },
  {
    route: "/planning/templates",
    title: tr('shell.features.demoDemoTour.linkedinTemplates'),
    body: tr('shell.features.demoDemoTour.libraryOfPostTemplatesRankedBy'),
  },
  {
    route: "/planning/review",
    title: tr('shell.features.demoDemoTour.aiReviewWorkshop'),
    body: tr('shell.features.demoDemoTour.agenticReviewLoopTheAiProposes'),
  },
  {
    route: "/publishing",
    title: tr('shell.features.demoDemoTour.publishingMediaStudio'),
    body: tr('shell.features.demoDemoTour.contentQueueBulkSchedulingGroupedMedia'),
  },
  {
    route: "/publishing/queue",
    title: tr('shell.features.demoDemoTour.evergreenRssQueue'),
    body: tr('shell.features.demoDemoTour.recyclingQueueContentCategoriesAndRss'),
  },
  {
    route: "/publishing/bulk",
    title: tr('shell.features.demoDemoTour.bulkScheduling'),
    body: tr('shell.features.demoDemoTour.importACsvAndScheduleDozens'),
  },
  {
    route: "/publishing/studio",
    title: tr('shell.features.demoDemoTour.bulkMediaStudio'),
    body: tr('shell.features.demoDemoTour.uploadSeveralMediaFilesThenSchedule'),
  },
  {
    route: "/publishing/wizard",
    title: tr('shell.features.demoDemoTour.aiPublishingAssistant'),
    body: tr('shell.features.demoDemoTour.stepByStepGuidedCreationTheme'),
  },
  {
    route: "/publishing/history",
    title: tr('shell.features.demoDemoTour.publishingHistory'),
    body: tr('shell.features.demoDemoTour.trackEveryPostSentItsStatus'),
  },
  {
    route: "/platforms",
    title: tr('shell.features.demoDemoTour.platforms'),
    body: tr('shell.features.demoDemoTour.connectFacebookInstagramTiktokYoutubeLin'),
  },

  // --- Engagement ---
  {
    route: "/inbox",
    title: tr('shell.features.demoDemoTour.socialInbox'),
    body: tr('shell.features.demoDemoTour.unifiedInboxSavedRepliesAndSla'),
  },
  {
    route: "/inbox/automation",
    title: tr('shell.features.demoDemoTour.inboxAutomation'),
    body: tr('shell.features.demoDemoTour.moderationRulesEngagementHoursAndRe'),
  },
  {
    route: "/community",
    title: tr('shell.features.demoDemoTour.aiCommunityManagement'),
    body: tr('shell.features.demoDemoTour.commentDashboardAssistedRepliesAndEditor'),
  },
  {
    route: "/community/canned",
    title: tr('shell.features.demoDemoTour.repliesMacros'),
    body: tr('shell.features.demoDemoTour.libraryOfReadyMadeRepliesAnd'),
  },
  {
    route: "/community/sla",
    title: tr('shell.features.demoDemoTour.slaSatisfaction'),
    body: tr('shell.features.demoDemoTour.responseTimePoliciesAndCsatSurveys'),
  },
  {
    route: "/listening",
    title: tr('shell.features.demoDemoTour.socialListening'),
    body: tr('shell.features.demoDemoTour.realTimeMentionFeedAndSocial'),
  },
  {
    route: "/veille",
    title: tr('shell.features.demoDemoTour.monitoringListening'),
    body: tr('shell.features.demoDemoTour.brandKeywordCompetitorMonitorsAggregated'),
  },
  {
    route: "/social",
    title: tr('shell.features.demoDemoTour.socialLeads'),
    body: tr('shell.features.demoDemoTour.spotAndQualifyProspectsComingFrom'),
  },
  {
    route: "/influencers",
    title: tr('shell.features.demoDemoTour.influencerCampaigns'),
    body: tr('shell.features.demoDemoTour.influencerShortlistBoardByStageAnd'),
  },
  {
    route: "/advocacy",
    title: tr('shell.features.demoDemoTour.ambassadorsAdvocacy'),
    body: tr('shell.features.demoDemoTour.shareFeedForYourEmployeesTracked'),
  },

  // --- Croissance ---
  {
    route: "/prospection/offres",
    title: tr('shell.features.demoDemoTour.offersProspecting'),
    body: tr('shell.features.demoDemoTour.describeEachProductBrandKingdomConnectio'),
  },
  {
    route: "/prospection",
    title: tr('shell.features.demoDemoTour.b2bDiscovery'),
    body: tr('shell.features.demoDemoTour.liveB2bSearchEmailPhoneEnrichment'),
  },
  {
    route: "/prospection/strategy",
    title: tr('shell.features.demoDemoTour.prospectingStrategy'),
    body: tr('shell.features.demoDemoTour.theAiPlanThatPrecedesThe'),
  },
  {
    route: "/prospection/recommend",
    title: tr('shell.features.demoDemoTour.recommendedProspects'),
    body: tr('shell.features.demoDemoTour.theAiRanksYourProspectsBy'),
  },
  {
    route: "/prospection/account-intelligence",
    title: tr('shell.features.demoDemoTour.accountIntelligence'),
    body: tr('shell.features.demoDemoTour.aiDossierPerCompanyNewsSignals'),
  },
  {
    route: "/prospection/calendar",
    title: tr('shell.features.demoDemoTour.prospectingCalendar'),
    body: tr('shell.features.demoDemoTour.datedMultiTouchSequenceEachTouchpoint'),
  },
  {
    route: "/prospection/visitors",
    title: tr('shell.features.demoDemoTour.identifiedVisitors'),
    body: tr('shell.features.demoDemoTour.identifyTheCompaniesVisitingYourSite'),
  },
  {
    route: "/prospection/job-change-alerts",
    title: tr('shell.features.demoDemoTour.jobChangeAlerts'),
    body: tr('shell.features.demoDemoTour.getAlertedWhenAContactChanges'),
  },
  {
    route: "/prospection/inbound-routers",
    title: tr('shell.features.demoDemoTour.inboundLeadRouting'),
    body: tr('shell.features.demoDemoTour.automaticallyAssignEachInboundLeadTo'),
  },
  {
    route: "/crm/contacts",
    title: tr('shell.features.demoDemoTour.crmLeads'),
    body: tr('shell.features.demoDemoTour.contactsCompaniesKanbanPipelineScoringIn'),
  },
  {
    route: "/crm/pipeline",
    title: tr('shell.features.demoDemoTour.salesPipeline'),
    body: tr('shell.features.demoDemoTour.kanbanViewOfYourOpportunitiesBy'),
  },
  {
    route: "/crm/signals",
    title: tr('shell.features.demoDemoTour.intentSignals'),
    body: tr('shell.features.demoDemoTour.aggregateBuyingSignalsVisitsOpensEngagem'),
  },
  {
    route: "/outreach",
    title: tr('shell.features.demoDemoTour.emailSms'),
    body: tr('shell.features.demoDemoTour.trackedMultiStepSequencesEmailSms'),
  },
  {
    route: "/outreach/unibox",
    title: tr('shell.features.demoDemoTour.uniboxReplies'),
    body: tr('shell.features.demoDemoTour.allRepliesFromYourEmailSms'),
  },
  {
    route: "/marketplace",
    title: tr('shell.features.demoDemoTour.servicesMarketplace'),
    body: tr('shell.features.demoDemoTour.providerDirectoryBookingsAndReviewsFind'),
  },

  // --- Publicités ---
  {
    route: "/ads/ai-create",
    title: tr('shell.features.demoDemoTour.createAnAdWithAi'),
    body: tr('shell.features.demoDemoTour.pasteAProductUrlTheAi'),
  },
  {
    route: "/ads",
    title: tr('shell.features.demoDemoTour.ads'),
    body: tr('shell.features.demoDemoTour.multiPlatformCampaignsABAuto'),
  },
  {
    route: "/ads/command",
    title: tr('shell.features.demoDemoTour.adsCommandCenter'),
    body: tr('shell.features.demoDemoTour.fullControlInOnePlaceEngagement'),
  },

  // --- Analyses ---
  {
    route: "/analytics",
    title: tr('shell.features.demoDemoTour.analyticsAi'),
    body: tr('shell.features.demoDemoTour.performanceConversionFunnelsMultiTouchAt'),
  },
  {
    route: "/reports",
    title: tr('shell.features.demoDemoTour.reportsBenchmark'),
    body: tr('shell.features.demoDemoTour.whiteLabelClientReadyReportsAnd'),
  },
  {
    route: "/analytics/geo",
    title: tr('shell.features.demoDemoTour.geoAiVisibility'),
    body: tr('shell.features.demoDemoTour.measureYourPresenceInAiSearch'),
  },
  {
    route: "/analytics/attribution",
    title: tr('shell.features.demoDemoTour.attributionRoi'),
    body: tr('shell.features.demoDemoTour.multiTouchAttributionOfConversionTo'),
  },
  {
    route: "/analytics/monitoring",
    title: tr('shell.features.demoDemoTour.monitoringAlerts'),
    body: tr('shell.features.demoDemoTour.monitorYourKpisContinuouslyAndGet'),
  },

  // --- IA & Automatisation ---
  {
    route: "/ai-assistant",
    title: tr('shell.features.demoDemoTour.aiAssistantCopilot'),
    body: tr('shell.features.demoDemoTour.driveEverythingInNaturalLanguageCreate'),
  },
  {
    route: "/ai-team/hq",
    title: tr('shell.features.demoDemoTour.n247AiTeamVirtualExperts'),
    body: tr('shell.features.demoDemoTour.autonomousMultiAgentSystemPlansAnd'),
  },
  {
    route: "/ai-team",
    title: tr('shell.features.demoDemoTour.aiTeamHub'),
    body: tr('shell.features.demoDemoTour.theEntryPointForYourVirtual'),
  },
  {
    route: "/ai-team/office",
    title: tr('shell.features.demoDemoTour.virtualOffice'),
    body: tr('shell.features.demoDemoTour.openSpaceViewOfYourAi'),
  },
  {
    route: "/ai-team/teams",
    title: tr('shell.features.demoDemoTour.aiTeams'),
    body: tr('shell.features.demoDemoTour.composeAndManageSeveralAiTeams'),
  },
  {
    route: "/ai-team/builder",
    title: tr('shell.features.demoDemoTour.teamBuilder'),
    body: tr('shell.features.demoDemoTour.buildATeamSeatBySeat'),
  },
  {
    route: "/ai-team/templates",
    title: tr('shell.features.demoDemoTour.teamTemplates'),
    body: tr('shell.features.demoDemoTour.startFromAReusableCompositionTemplate'),
  },
  {
    route: "/ai-team/pool",
    title: tr('shell.features.demoDemoTour.taskQueue'),
    body: tr('shell.features.demoDemoTour.theSharedTaskPoolPrioritizeAnd'),
  },
  {
    route: "/ai-team/deliverables",
    title: tr('shell.features.demoDemoTour.deliverables'),
    body: tr('shell.features.demoDemoTour.retrieveAndApproveTheDeliverablesProduce'),
  },
  {
    route: "/ai-team/schedules",
    title: tr('shell.features.demoDemoTour.schedules'),
    body: tr('shell.features.demoDemoTour.defineEachAgentSActivityWindows'),
  },
  {
    route: "/ai-team/control",
    title: tr('shell.features.demoDemoTour.controlConsole'),
    body: tr('shell.features.demoDemoTour.masterSwitchMandateCadenceAndBudget'),
  },
  {
    route: "/ai-team/audit",
    title: tr('shell.features.demoDemoTour.aiAuditLog'),
    body: tr('shell.features.demoDemoTour.fullTraceabilityEveryAiDecisionAnd'),
  },
  {
    route: "/orchestration",
    title: tr('shell.features.demoDemoTour.multiAgentOrchestration'),
    body: tr('shell.features.demoDemoTour.fromAGoalTheAiBuilds'),
  },
  {
    route: "/approvals",
    title: tr('shell.features.demoDemoTour.aiApprovals'),
    body: tr('shell.features.demoDemoTour.queueOfAiProposedActionsAwaiting'),
  },
  {
    route: "/staff-questions",
    title: tr('shell.features.demoDemoTour.aiTeamQuestions'),
    body: tr('shell.features.demoDemoTour.whenAnAgentNeedsAClarification'),
  },
  {
    route: "/grants",
    title: tr('shell.features.demoDemoTour.accessRequests'),
    body: tr('shell.features.demoDemoTour.agentsExplicitlyRequestAdditionalAccessO'),
  },
  {
    route: "/ai-activity",
    title: tr('shell.features.demoDemoTour.aiActivity'),
    body: tr('shell.features.demoDemoTour.theRealTimeStreamOfEverything'),
  },
  {
    route: "/ai/content-studio",
    title: tr('shell.features.demoDemoTour.aiContentStudio'),
    body: tr('shell.features.demoDemoTour.postGenerationGuidedByYourBrand'),
  },
  {
    route: "/ai/ghostwriter",
    title: tr('shell.features.demoDemoTour.ghostwriter'),
    body: tr('shell.features.demoDemoTour.writeInYourStyleFromA'),
  },
  {
    route: "/ai/repurpose",
    title: tr('shell.features.demoDemoTour.contentReuse'),
    body: tr('shell.features.demoDemoTour.turnAnArticleAVideoOr'),
  },
  {
    route: "/ai/hook-library",
    title: tr('shell.features.demoDemoTour.hookLibrary'),
    body: tr('shell.features.demoDemoTour.hundredsOfProvenHooksFilterableBy'),
  },
  {
    route: "/ai/longform",
    title: tr('shell.features.demoDemoTour.longFormStudio'),
    body: tr('shell.features.demoDemoTour.writeBlogArticlesNewslettersAndStructure'),
  },
  {
    route: "/ai/canvas",
    title: tr('shell.features.demoDemoTour.canvas'),
    body: tr('shell.features.demoDemoTour.aFreeFormWritingSpaceSide'),
  },
  {
    route: "/ai/grid",
    title: tr('shell.features.demoDemoTour.gridBulkGeneration'),
    body: tr('shell.features.demoDemoTour.generateDozensOfVariantsInBulk'),
  },
  {
    route: "/ai/brand-voice",
    title: tr('shell.features.demoDemoTour.brandVoice'),
    body: tr('shell.features.demoDemoTour.defineAndTrainYourBrandVoice'),
  },
  {
    route: "/ai/ab-copy",
    title: tr('shell.features.demoDemoTour.aBCopy'),
    body: tr('shell.features.demoDemoTour.produceSeveralVersionsOfAText'),
  },
  {
    route: "/ai/suggestions",
    title: tr('shell.features.demoDemoTour.aiSuggestions'),
    body: tr('shell.features.demoDemoTour.contextualContentAndOptimizationRecommen'),
  },
  {
    route: "/ai/personalization",
    title: tr('shell.features.demoDemoTour.personalization'),
    body: tr('shell.features.demoDemoTour.automaticallyAdaptMessageAndToneTo'),
  },
  {
    route: "/ai/community-ai",
    title: tr('shell.features.demoDemoTour.communityAi'),
    body: tr('shell.features.demoDemoTour.theAiDraftsRepliesToComments'),
  },
  {
    route: "/ai/rag-documents",
    title: tr('shell.features.demoDemoTour.ragDocuments'),
    body: tr('shell.features.demoDemoTour.feedTheAiYourOwnDocuments'),
  },
  {
    route: "/ai/memory",
    title: tr('shell.features.demoDemoTour.aiMemory'),
    body: tr('shell.features.demoDemoTour.persistentMemoryThatKeepsYourContext'),
  },
  {
    route: "/workflows",
    title: tr('shell.features.demoDemoTour.workflowsRpa'),
    body: tr('shell.features.demoDemoTour.automateEndToEndReadyMade'),
  },
  {
    route: "/workflows/studio",
    title: tr('shell.features.demoDemoTour.flowStudio'),
    body: tr('shell.features.demoDemoTour.dragAndDropAgenticEditorBpmn'),
  },
  {
    route: "/automations",
    title: tr('shell.features.demoDemoTour.automations'),
    body: tr('shell.features.demoDemoTour.conditionalPostPublicationRulesDisabledB'),
  },
  {
    route: "/learning",
    title: tr('shell.features.demoDemoTour.aiLearning'),
    body: tr('shell.features.demoDemoTour.whatWorksPerPlatformConfirmedWinning'),
  },

  // --- Paramètres ---
  {
    route: "/settings/profile",
    title: tr('shell.features.demoDemoTour.profile'),
    body: tr('shell.features.demoDemoTour.yourPersonalInformationLanguageAndDispla'),
  },
  {
    route: "/settings/team",
    title: tr('shell.features.demoDemoTour.teamInvitations'),
    body: tr('shell.features.demoDemoTour.inviteColleaguesAssignRolesAndManage'),
  },
  {
    route: "/settings/organization",
    title: tr('shell.features.demoDemoTour.organization'),
    body: tr('shell.features.demoDemoTour.organizationIdentityNameDomainPlanAnd'),
  },
  {
    route: "/settings/notifications",
    title: tr('shell.features.demoDemoTour.notifications'),
    body: tr('shell.features.demoDemoTour.choosePreciselyWhenAndHowTo'),
  },
  {
    route: "/settings/roles",
    title: tr('shell.features.demoDemoTour.rolesPermissions'),
    body: tr('shell.features.demoDemoTour.createCustomRolesAndAPermission'),
  },
  {
    route: "/settings/delegations",
    title: tr('shell.features.demoDemoTour.accessDelegationManagedAccounts'),
    body: tr('shell.features.demoDemoTour.grantAnExternalManagerVeryGranular'),
  },
  {
    route: "/settings/connections",
    title: tr('shell.features.demoDemoTour.networkConnections'),
    body: tr('shell.features.demoDemoTour.linkAndReconnectYourSocialAccounts'),
  },
  {
    route: "/settings/connectors",
    title: tr('shell.features.demoDemoTour.connectors'),
    body: tr('shell.features.demoDemoTour.configureTheDataConnectorsAndThird'),
  },
  {
    route: "/settings/integrations",
    title: tr('shell.features.demoDemoTour.integrations'),
    body: tr('shell.features.demoDemoTour.plugInYourToolsCrmSlack'),
  },
  {
    route: "/settings/developers",
    title: tr('shell.features.demoDemoTour.apiDevelopers'),
    body: tr('shell.features.demoDemoTour.apiKeysOauth2AppsAndWebhooks'),
  },
  {
    route: "/settings/mailboxes",
    title: tr('shell.features.demoDemoTour.sendingMailboxes'),
    body: tr('shell.features.demoDemoTour.smtpSendingMailboxesAndDomainPurchase'),
  },
  {
    route: "/settings/subscription",
    title: tr('shell.features.demoDemoTour.subscriptionPaywall'),
    body: tr('shell.features.demoDemoTour.plansCapsAiCreditsReverseTrial'),
  },
  {
    route: "/settings/billing",
    title: tr('shell.features.demoDemoTour.billing'),
    body: tr('shell.features.demoDemoTour.yourAiCreditsPurchasesAndBilling'),
  },
  {
    route: "/settings/credits",
    title: tr('shell.features.demoDemoTour.aiCredits'),
    body: tr('shell.features.demoDemoTour.trackAiCreditConsumptionPerFeature'),
  },
  {
    route: "/settings/credits/budgets",
    title: tr('shell.features.demoDemoTour.perUserBudgets'),
    body: tr('shell.features.demoDemoTour.setAiCreditCapsPerUser'),
  },
  {
    route: "/settings/referral",
    title: tr('shell.features.demoDemoTour.referralAffiliation'),
    body: tr('shell.features.demoDemoTour.twoSidedReferralAffiliateProgramTracked'),
  },
  {
    route: "/settings/ai-registry",
    title: tr('shell.features.demoDemoTour.aiRegistry'),
    body: tr('shell.features.demoDemoTour.agentsSkillsPromptsModelsAndTools'),
  },
  {
    route: "/settings/models",
    title: tr('shell.features.demoDemoTour.aiModelsAdmin'),
    body: tr('shell.features.demoDemoTour.chooseTheAvailableModelsTheirCredit'),
  },
  {
    route: "/settings/byok",
    title: tr('shell.features.demoDemoTour.llmKeysByok'),
    body: tr('shell.features.demoDemoTour.bringYourOwnLlmKeysBill'),
  },
  {
    route: "/settings/governance",
    title: tr('shell.features.demoDemoTour.aiGovernance'),
    body: tr('shell.features.demoDemoTour.guardrailsAutonomyLimitsAndAiUsage'),
  },
  {
    route: "/settings/content-engine",
    title: tr('shell.features.demoDemoTour.contentEngine'),
    body: tr('shell.features.demoDemoTour.contentAutomationPolicyEverythingOffBy'),
  },
  {
    route: "/settings/review-policy",
    title: tr('shell.features.demoDemoTour.reviewPolicy'),
    body: tr('shell.features.demoDemoTour.autoPublishingMasterSwitchRulesAnd'),
  },
  {
    route: "/settings/trust-center",
    title: tr('shell.features.demoDemoTour.trustCenter'),
    body: tr('shell.features.demoDemoTour.consentRegisterStopUnsubscribeListsPer'),
  },
];

/**
 * Fixed launcher position. On md+ it clears the desktop sidebar rail (w-60, or
 * w-16 when collapsed) — at `left-4` it covered the rail's bottom items and
 * intercepted their clicks (prod I1-R17: the last « Plus » sub-group was
 * unclickable). The mobile drawer is an overlay, so `left-4` stays below md.
 * Vertically it sits above the app footer (one or two wrapped lines, <= 3.25rem): at
 * `bottom-4` it covered the footer's left items (brand, version link) and intercepted
 * their clicks (prod S1 SHL-116, AUDIT-s1a6).
 */
export function launcherPositionClass(sidebarCollapsed: boolean): string {
  return `fixed bottom-14 left-4 z-40 flex flex-col items-start gap-2 ${
    sidebarCollapsed ? 'md:left-20' : 'md:left-64'
  }`;
}

export function DemoTour() {
  const sidebarCollapsed = useUIStore((st) => st.sidebarCollapsed);
  const { t: tx } = useT();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goto = useCallback(
    (idx: number) => {
      const clamped = Math.max(0, Math.min(STEPS.length - 1, idx));
      setI(clamped);
      navigate(resolveStepRoute(STEPS[clamped]));
    },
    [navigate],
  );

  const start = () => {
    setActive(true);
    setI(0);
    navigate(resolveStepRoute(STEPS[0]));
  };
  const rec = useScreenRecorder();
  const { recording, stop: recStop, start: recStart } = rec;
  const [recMode, setRecMode] = useState<"none" | "full" | "single">("none");
  const [menuOpen, setMenuOpen] = useState(false);
  const [pick, setPick] = useState(0);
  const resetTour = useCallback(() => {
    setActive(false);
    setAuto(false);
    setRecMode("none");
    if (timer.current) clearTimeout(timer.current);
  }, []);
  const stop = useCallback(() => {
    resetTour();
    if (recording) recStop(); // finalise + télécharge
  }, [resetTour, recording, recStop]);

  const startFullRecording = async () => {
    setMenuOpen(false);
    const ok = await recStart(
      `demo-karexia-complete-${todayStamp()}.webm`,
      resetTour,
    );
    if (!ok) return;
    setRecMode("full");
    setActive(true);
    setI(0);
    setAuto(true);
    navigate(resolveStepRoute(STEPS[0]));
  };

  const startSingleRecording = async (idx: number) => {
    setMenuOpen(false);
    const st = STEPS[idx];
    const ok = await recStart(
      `demo-karexia-${routeSlug(st.route)}-${todayStamp()}.webm`,
      resetTour,
    );
    if (!ok) return;
    setRecMode("single");
    setI(idx);
    navigate(resolveStepRoute(st));
  };

  // Mode fonctionnalité unique : ~9 s sur l'écran puis arrêt + téléchargement.
  useEffect(() => {
    if (recMode !== "single" || !recording) return;
    const t = setTimeout(() => recStop(), 9000);
    return () => clearTimeout(t);
  }, [recMode, recording, recStop]);

  // Auto-advance.
  useEffect(() => {
    if (!active || !auto) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (i >= STEPS.length - 1) {
        stop();
      } else {
        goto(i + 1);
      }
    }, 4500);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [active, auto, i, goto, stop]);

  const recBar = recording ? (
    <div
      role="status"
      style={{ zIndex: TOUR_Z + 10 }}
      className="fixed left-1/2 top-3 flex -translate-x-1/2 items-center gap-3 rounded-full border border-border bg-surface px-4 py-2 text-sm shadow-elevation-3"
    >
      <span className="flex items-center gap-1.5 font-semibold text-red-600">
        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" />{tx('shell.features.demoDemoTour.recV', { v: formatElapsed(rec.elapsed) })}
      </span>
      {recMode === "single" && (
        <span className="text-muted">{STEPS[i].title}</span>
      )}
      <button
        type="button"
        onClick={stop}
        className="rounded-token bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
      >{tx('shell.features.demoDemoTour.stopDownload')}</button>
    </div>
  ) : null;

  const errBanner = rec.error ? (
    <div
      role="alert"
      style={{ zIndex: TOUR_Z + 10 }}
      className="fixed left-1/2 top-3 flex w-[min(92vw,32rem)] -translate-x-1/2 items-start gap-2 rounded-token-lg border border-border bg-surface p-3 text-sm text-foreground shadow-elevation-3"
    >
      <span className="flex-1">{rec.error}</span>
      <button
        type="button"
        onClick={rec.clearError}
        aria-label={tx('shell.features.demoDemoTour.closeTheMessage')}
        className="text-muted hover:text-foreground"
      >
        ✕
      </button>
    </div>
  ) : null;

  if (recMode === "single") {
    return (
      <>
        {recBar}
        {errBanner}
      </>
    );
  }

  if (!active) {
    return (
      <>
        {recBar}
        {errBanner}
        <div className={launcherPositionClass(sidebarCollapsed)}>
          {menuOpen && (
            <div
              role="menu"
              aria-label={tx('shell.features.demoDemoTour.demoOptions')}
              className="w-72 rounded-token-lg border border-border bg-surface p-3 text-sm shadow-elevation-3"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  start();
                }}
                className="w-full rounded-token px-2 py-1.5 text-left font-medium text-foreground hover:bg-surface-2"
              >{tx('shell.features.demoDemoTour.startTheGuidedDemo')}</button>
              <hr className="my-2 border-border" />
              <p className="px-2 text-xs text-muted">{tx('shell.features.demoDemoTour.tipInTheBrowserSSharing')}</p>
              <button
                type="button"
                role="menuitem"
                onClick={startFullRecording}
                disabled={!rec.supported}
                className="mt-1 w-full rounded-token px-2 py-1.5 text-left font-medium text-foreground hover:bg-surface-2 disabled:opacity-40"
              >{tx('shell.features.demoDemoTour.recordTheDemoAsAVideo')}</button>
              <div className="mt-2 px-2">
                <label
                  className="text-xs font-medium text-muted"
                  htmlFor="demo-rec-feature"
                >{tx('shell.features.demoDemoTour.recordASingleFeature9S')}</label>
                <div className="mt-1 flex gap-1.5">
                  <select
                    id="demo-rec-feature"
                    value={pick}
                    onChange={(e) => setPick(Number(e.target.value))}
                    className="min-w-0 flex-1 rounded-token border border-border bg-surface px-2 py-1 text-xs text-foreground"
                  >
                    {STEPS.map((st, idx) => (
                      // Routes repeat across steps (/calendar ×3,
                      // /content/diagram-studio ×2) — key on the position.
                      <option key={`${idx}:${st.route}`} value={idx}>
                        {st.title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => startSingleRecording(pick)}
                    disabled={!rec.supported}
                    className="rounded-token bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                  >{tx('shell.features.demoDemoTour.rec')}</button>
                </div>
              </div>
              {!rec.supported && (
                <p className="mt-2 px-2 text-xs text-red-600">
                  {RECORDER_UNSUPPORTED_MSG}
                </p>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={tx('shell.features.demoDemoTour.startTheGuidedDemo2')}
            aria-expanded={menuOpen}
            className="flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-2 text-sm font-medium text-foreground shadow-elevation-2 transition hover:bg-surface-2"
          >{tx('shell.features.demoDemoTour.guidedDemo')}</button>
        </div>
      </>
    );
  }

  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  return (
    <>
      {recBar}
      {errBanner}
      <div
        role="dialog"
        aria-label={tx('shell.features.demoDemoTour.guidedDemo2')}
        style={{ zIndex: TOUR_Z }}
        className="fixed inset-x-0 bottom-4 mx-auto w-[min(92vw,32rem)] rounded-token-lg border border-border bg-surface p-4 shadow-elevation-3"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted">{tx('shell.features.demoDemoTour.demoVLength', { v: i + 1, length: STEPS.length })}
          </span>
          <button
            type="button"
            onClick={stop}
            aria-label={tx('shell.features.demoDemoTour.closeTheDemo')}
            className="rounded-token px-2 py-0.5 text-sm text-muted hover:bg-surface-2"
          >
            ✕
          </button>
        </div>
        <h3 className="mt-1 text-lg font-semibold text-foreground">
          {step.title}
        </h3>
        <p className="mt-1 text-sm text-muted">{step.body}</p>

        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all"
            style={{ width: `${((i + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          {recording ? (
            <span className="text-xs font-medium text-red-600">{tx('shell.features.demoDemoTour.recordingInProgress')}</span>
          ) : (
            <label className="flex items-center gap-1.5 text-xs text-muted">
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-primary"
              />{tx('shell.features.demoDemoTour.autoplay')}</label>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goto(i - 1)}
              disabled={i === 0}
              className="rounded-token border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2 disabled:opacity-40"
            >{tx('shell.features.demoDemoTour.previous')}</button>
            {last ? (
              <button
                type="button"
                onClick={stop}
                className="rounded-token bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
              >{tx('shell.features.demoDemoTour.finish')}</button>
            ) : (
              <button
                type="button"
                onClick={() => goto(i + 1)}
                className="rounded-token bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
              >{tx('shell.features.demoDemoTour.next')}</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
